// People › Elections — browse index (#282).
// Lists every federal general election + by-election from the `elections`
// index. Small fixed set (216), fetched in full; client-side filter (type) +
// sortable columns. Each row links to the per-election detail page.

import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, CircularProgress, Chip, Alert,
  Table, TableBody, TableRow, TableCell, TableHead, TableContainer,
  TableSortLabel, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import { getElections } from '../services/api';
import useTableSort from '../hooks/useTableSort';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU',
  { day: '2-digit', month: 'short', year: 'numeric' }) : '');

const SORT_KEYS = {
  Year: (e) => Number(e.Year) || 0,
  Date: (e) => e.Date || '',
  Type: (e) => (e.is_by_election ? 1 : 0),
  Seat: (e) => e.Division || '',
  'Largest party': (e) => e.largest_party || '',
  Seats: (e) => Number(e.total_seats) || 0,
};

export default function ElectionsSearch() {
  const [elections, setElections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const data = await getElections();
        setElections(data.elections || []);
      } catch (err) {
        setError('Failed to load elections data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'general') return elections.filter((e) => !e.is_by_election);
    if (filter === 'by') return elections.filter((e) => e.is_by_election);
    return elections;
  }, [elections, filter]);

  const { order, orderBy, sortedRows, handleSort } = useTableSort(
    filtered, (e, col) => SORT_KEYS[col](e));

  const columns = ['Year', 'Date', 'Type', 'Seat', 'Largest party', 'Seats'];
  const nGeneral = elections.filter((e) => !e.is_by_election).length;
  const nBy = elections.length - nGeneral;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <HowToVoteIcon color="primary" /> Federal elections
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Every general election ({nGeneral}) and by-election ({nBy}) since Federation. Select one to
        see its results — party seats and the per-division winner, two-party-preferred and swing.
      </Typography>

      <ToggleButtonGroup size="small" value={filter} exclusive sx={{ mb: 2 }}
        onChange={(_, v) => v && setFilter(v)}>
        <ToggleButton value="all">All ({elections.length})</ToggleButton>
        <ToggleButton value="general">General ({nGeneral})</ToggleButton>
        <ToggleButton value="by">By-elections ({nBy})</ToggleButton>
      </ToggleButtonGroup>

      <Paper elevation={1} sx={{ borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c} sx={{ fontWeight: 600 }}
                    sortDirection={orderBy === c ? order : false}>
                    <TableSortLabel active={orderBy === c}
                      direction={orderBy === c ? order : 'asc'}
                      onClick={() => handleSort(c)}>{c}</TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((e) => (
                <TableRow key={e.id} hover component={RouterLink} to={`/elections/${e.id}`}
                  sx={{ textDecoration: 'none', cursor: 'pointer' }}>
                  <TableCell>{e.Year}</TableCell>
                  <TableCell>{fmtDate(e.Date)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={e.is_by_election ? 'By-election' : 'General'}
                      color={e.is_by_election ? 'default' : 'primary'}
                      variant={e.is_by_election ? 'outlined' : 'filled'} />
                  </TableCell>
                  <TableCell>{e.Division || '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {e.largest_party_colour && (
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%',
                          bgcolor: e.largest_party_colour, flexShrink: 0 }} />
                      )}
                      {e.largest_party || '—'}
                    </Box>
                  </TableCell>
                  <TableCell>{e.total_seats || (e.is_by_election ? 1 : '')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
